import { authenticatedFetch } from '../lib/apiClient';
import type {
  ProjectItemRecord,
  CreateItemPayload,
  UpdateItemPayload,
  ItemType,
} from '../types/viewerProjectItems';

const BASE = '/api/admin/viewer-projects';

/**
 * Parse a JSON response and throw an Error with the backend message if not ok.
 */
async function parseOrThrow<T>(response: Response): Promise<T> {
  const json = await response.json();

  if (!response.ok) {
    const message =
      (typeof json.error === 'string' ? json.error : json.error?.message) ||
      `Request failed with status ${response.status}`;
    throw new Error(message);
  }

  return json.data as T;
}

/**
 * Fetch all project items for a company/product, optionally filtered by type.
 * Supports AbortController signal forwarding for cancellation.
 */
export async function listItems(
  companyId: string,
  productId: string,
  type?: ItemType,
  signal?: AbortSignal,
): Promise<ProjectItemRecord[]> {
  const url = type
    ? `${BASE}/${companyId}/${productId}/items?type=${type}`
    : `${BASE}/${companyId}/${productId}/items`;

  const response = await authenticatedFetch(url, { method: 'GET', signal });
  return parseOrThrow<ProjectItemRecord[]>(response);
}

/**
 * Create a new project item.
 */
export async function createItem(
  companyId: string,
  productId: string,
  payload: CreateItemPayload,
): Promise<ProjectItemRecord> {
  const response = await authenticatedFetch(
    `${BASE}/${companyId}/${productId}/items`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
  );
  return parseOrThrow<ProjectItemRecord>(response);
}

/**
 * Partially update an existing project item.
 */
export async function updateItem(
  companyId: string,
  productId: string,
  itemId: string,
  fields: UpdateItemPayload,
): Promise<ProjectItemRecord> {
  const response = await authenticatedFetch(
    `${BASE}/${companyId}/${productId}/items/${itemId}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(fields),
    },
  );
  return parseOrThrow<ProjectItemRecord>(response);
}

/**
 * Delete a project item.
 */
export async function deleteItem(
  companyId: string,
  productId: string,
  itemId: string,
): Promise<void> {
  const response = await authenticatedFetch(
    `${BASE}/${companyId}/${productId}/items/${itemId}`,
    { method: 'DELETE' },
  );

  if (!response.ok) {
    const json = await response.json();
    const message =
      (typeof json.error === 'string' ? json.error : json.error?.message) ||
      `Request failed with status ${response.status}`;
    throw new Error(message);
  }
}

/**
 * Reorder project items by providing the desired ordered array of item IDs.
 */
export async function reorderItems(
  companyId: string,
  productId: string,
  orderedIds: string[],
): Promise<void> {
  const response = await authenticatedFetch(
    `${BASE}/${companyId}/${productId}/items/reorder`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids: orderedIds }),
    },
  );

  if (!response.ok) {
    const json = await response.json();
    const message =
      (typeof json.error === 'string' ? json.error : json.error?.message) ||
      `Request failed with status ${response.status}`;
    throw new Error(message);
  }
}

/**
 * Upload an image for a project item using the 3-step flow:
 * 1. POST upload intent with file metadata → receive presigned URL + assetId
 * 2. PUT file blob to the presigned S3 URL
 * 3. POST complete-upload with assetId → receive final imageUrl
 */
export async function uploadImage(
  companyId: string,
  productId: string,
  itemId: string,
  file: File,
): Promise<string> {
  // Step 1: Upload intent
  const intentResponse = await authenticatedFetch(
    `${BASE}/${companyId}/${productId}/items/${itemId}/upload-image`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fileName: file.name,
        contentType: file.type,
        sizeBytes: file.size,
      }),
    },
  );
  const intentData = await parseOrThrow<{ uploadUrl: string; assetId: string }>(intentResponse);

  // Step 2: Upload file to S3 presigned URL
  const uploadResponse = await fetch(intentData.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });

  if (!uploadResponse.ok) {
    throw new Error('Image upload to storage failed.');
  }

  // Step 3: Complete upload
  const completeResponse = await authenticatedFetch(
    `${BASE}/${companyId}/${productId}/items/${itemId}/complete-upload`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ assetId: intentData.assetId }),
    },
  );
  const completeData = await parseOrThrow<{ imageUrl: string }>(completeResponse);

  return completeData.imageUrl;
}
